import React, {useEffect, useRef} from 'react'
import _ from 'lodash'
import Request from 'apis/middleware/request'
import {getStoredDefaultCountryId, setCoordinatesStore, setStoredDefaultCountryId,} from '../../helpers/localStorage'
import {useAppConfig} from '../../modules/local/AppConfigContext'
import {IS_CORDOVA} from '../../envs/_current/config'

const CACHE_KEY = '_wCoordinatesKey'
const CACHE_TTL = 30 * 60 * 1000

// ─── CACHE ───────────────────────────────────────────────────────────────────
function getCachedCoordinates() {
    try {
        const raw = localStorage.getItem(CACHE_KEY)
        if (!raw) return null
        const data = JSON.parse(raw)
        if (!data?.timestamp) return null
        if (Date.now() - data.timestamp > CACHE_TTL) return null
        return data
    } catch {
        return null
    }
}

function setCachedCoordinates({latitude, longitude, countryId}) {
    try {
        localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({latitude, longitude, countryId, timestamp: Date.now()})
        )
    } catch {
    }
}

// ─── GEO ─────────────────────────────────────────────────────────────────────
function handlePosition(position) {
    const {latitude, longitude} = position.coords
    if (!latitude || !longitude) return null
    setCoordinatesStore({latitude, longitude})
    return {latitude, longitude}
}

// ─── COUNTRY ─────────────────────────────────────────────────────────────────
async function fetchCountry(lat, lon, countries, signal) {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
    try {
        let data
        if (IS_CORDOVA) {
            const res = await fetch(url)
            if (!res.ok) return null
            data = await res.json()
        } else {
            const res = await Request.get(url, {signal})
            data = _.get(res, 'data')
        }
        const code = _.get(data, 'address.country_code', '').toUpperCase()
        if (!code) return null
        const country = countries.find((c) => c.country_code === code)
        return country?.id ?? null
    } catch (e) {
        if (
            e?.name === 'AbortError' ||
            e?.message?.includes('abort') ||
            e?.message?.includes('cancel') ||
            e?.code === 20
        ) {
            return null
        }
        console.error('fetchCountry error:', e)
        return null
    }
}

// ─── HOOK (tách logic ra khỏi render tree) ───────────────────────────────────
// Dùng custom hook thay vì wrapper component → tránh hoàn toàn lỗi
// "Cannot update a component while rendering a different component"
// vì hook chạy trong cùng component, không có parent/child setState conflict.
export function useCoordinates() {
    const abortRef = useRef(null)
    const ranRef = useRef(false)
    const {countries = []} = useAppConfig('countries')

    useEffect(() => {
        if (!countries.length) return
        if (ranRef.current) return
        ranRef.current = true

        let isMounted = true

        if (!IS_CORDOVA) {
            try {
                abortRef.current = new AbortController()
            } catch {
            }
        }

        const getSignal = () => abortRef.current?.signal ?? null

        const run = async () => {
            const cached = getCachedCoordinates()

            if (cached) {
                setCoordinatesStore({
                    latitude: cached.latitude,
                    longitude: cached.longitude,
                })

                const storedId = getStoredDefaultCountryId()

                if (cached.countryId) {
                    if (storedId !== cached.countryId) {
                        setStoredDefaultCountryId(cached.countryId)
                    }
                    return
                }

                if (cached.latitude && countries.length) {
                    const countryId = await fetchCountry(
                        cached.latitude,
                        cached.longitude,
                        countries,
                        getSignal()
                    )
                    if (!isMounted || !countryId) return
                    setStoredDefaultCountryId(countryId)
                    setCachedCoordinates({
                        latitude: cached.latitude,
                        longitude: cached.longitude,
                        countryId,
                    })
                }
                return
            }

            if (!navigator?.geolocation) return

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const coords = handlePosition(position)
                    if (!coords || !isMounted) return

                    setCoordinatesStore(coords)

                    if (!countries.length) return

                    const countryId = await fetchCountry(
                        coords.latitude,
                        coords.longitude,
                        countries,
                        getSignal()
                    )

                    if (!isMounted || !countryId) return

                    setStoredDefaultCountryId(countryId)
                    setCachedCoordinates({
                        latitude: coords.latitude,
                        longitude: coords.longitude,
                        countryId,
                    })
                },
                (error) => {
                    console.warn('Geolocation error:', error.code, error.message)
                },
                {
                    enableHighAccuracy: false,
                    timeout: 10000,
                    maximumAge: 15 * 60 * 1000,
                }
            )
        }

        run().catch(() => {
        })

        return () => {
            isMounted = false
            if (!IS_CORDOVA && abortRef.current) {
                try {
                    abortRef.current.abort()
                } catch {
                }
            }
        }
    }, [countries])
}

// ─── COMPONENT (giữ lại để backward compatible với các nơi khác dùng) ────────
// FIX LỖI CHÍNH: return <>{children}</> thay vì return children
// Khi return children trực tiếp, React đôi khi xử lý nó như một render call
// của component cha, dẫn đến setState conflict trong reconciliation phase.
const Coordinates = ({children}) => {
    useCoordinates()
    // ✅ Wrap bằng Fragment → React tạo một boundary rõ ràng,
    //    tránh hoàn toàn lỗi "Cannot update a component while rendering another"
    return <>{children}</>
}

export default Coordinates